// Wizard — 5 steps: Brand → Service → Country/City → Budget → Offer.
// Ports the design's wizard.jsx, but loads brands + offers from the backend
// instead of hardcoding service/offer chips. New campaign metadata
// (website/country/city/budgetMonthly) flows into job.options.
import { useEffect, useMemo, useState } from 'react';
import { PALETTE } from '../palette.js';
import { Button, Pill } from '../components/primitives.jsx';
import { IconChevron, IconSpark, IconMap } from '../components/icons.jsx';
import { api, getToken, setToken } from '../api.js';
import { safeFailureMessage } from '../safe-failure.js';
import {
  campaignLaunchReadinessPresentation,
  createCampaignLaunchReadinessError,
} from '../campaign-launch-readiness.js';

const PROVISION_PHASE_LABELS = {
  github_create: 'Creating GitHub repo from template',
  github_clone: 'Cloning new repo onto the server',
  hosting_skipped: 'Source ready; custom lead-site hosting is not configured',
  vercel_deploy: 'Historical hosting step unavailable',
  vercel_alias: 'Historical hosted URL ignored',
  vercel_skipped: 'Custom lead-site hosting is not configured',
  vercel_failed: 'Historical hosting step unavailable',
  done: 'Brand source ready',
};

async function streamBrandResearch({ url, onProgress }) {
  const token = getToken();
  // Backend validates url.startsWith('http'); the wizard input may be a bare
  // domain like "torontogeneralcontractors.com". Normalize so users don't
  // have to type the protocol.
  const normalizedUrl = normalizeWebsiteUrl(url);
  const res = await fetch('/api/brand-research', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ url: normalizedUrl }),
  });
  if (!res.ok) throw new Error(`Brand research failed (${res.status})`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let result = null;
  let errMsg = null;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const line = frame.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      let evt; try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }
      if (evt.type === 'progress') onProgress?.(evt);
      else if (evt.type === 'result') result = evt.brain;
      else if (evt.type === 'error')  errMsg = evt.message || 'Brand research failed';
    }
  }
  if (errMsg) throw new Error(errMsg);
  if (!result) throw new Error('Brand research ended without a result');
  return result;
}

async function streamProvision({ brandSlug, brandName, onPhase }) {
  const token = getToken();
  const res = await fetch('/api/brands/provision/stream', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ brandSlug, brandName }),
  });
  if (!res.ok) throw new Error(`Provision failed (${res.status})`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let final = null;
  let errMsg = null;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const line = frame.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      let evt;
      try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }
      if (evt.type === 'phase') onPhase(evt);
      else if (evt.type === 'result') final = evt;
      else if (evt.type === 'error') errMsg = evt.message;
    }
  }
  if (errMsg) throw new Error(errMsg);
  if (!final) throw new Error('Provision stream ended without a result');
  return final;
}

const TOTAL_STEPS = 5;

const SERVICE_GLYPHS = {
  kitchen: '◱', bathroom: '◲', basement: '▱', roof: '△',
  windows: '▥', addition: '◰', deck: '▤', flooring: '▦', sunroom: '☀',
};
const SERVICE_LABELS = {
  kitchen: 'Kitchen remodel', bathroom: 'Bathroom remodel', basement: 'Basement finish',
  roof: 'Roofing', windows: 'Windows & doors', addition: 'Addition / ADU',
  deck: 'Deck & outdoor', flooring: 'Flooring', sunroom: 'Sunroom',
};
const COUNTRY_OPTIONS = [
  { value: 'CA', glyph: 'CA', label: 'Canada' },
  { value: 'US', glyph: 'US', label: 'United States' },
  { value: 'MX', glyph: 'MX', label: 'Mexico' },
];
const COUNTRY_CITY_PRESETS = {
  CA: { placeholder: 'e.g. Toronto, ON', cities: ['Toronto, ON', 'Vancouver, BC', 'Calgary, AB', 'Montreal, QC'] },
  US: { placeholder: 'e.g. Austin, TX', cities: ['Austin, TX', 'Denver, CO', 'Tampa, FL', 'Seattle, WA'] },
  MX: { placeholder: 'e.g. Monterrey, NL', cities: ['Monterrey, NL', 'Guadalajara, JA', 'Mexico City, CMX', 'Merida, YUC'] },
};

function WizardHeader({ step, onExit }) {
  return (
    <div style={{
      height: 56, padding: '0 32px', display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', borderBottom: `1px solid ${PALETTE.lineSoft}`,
      background: PALETTE.bg, position: 'sticky', top: 0, zIndex: 2,
    }}>
      <button onClick={onExit} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: PALETTE.ink3, fontSize: 13, fontFamily: 'Inter, sans-serif', padding: 0,
      }}>← Save and exit</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: PALETTE.ink3, letterSpacing: '0.05em' }}>
          <span style={{ color: PALETTE.ink }}>{String(step + 1).padStart(2, '0')}</span> / {String(TOTAL_STEPS).padStart(2, '0')}
        </div>
        <div style={{ width: 180, height: 4, background: PALETTE.surfaceAlt, borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${((step + 1) / TOTAL_STEPS) * 100}%`, background: PALETTE.accent, transition: 'width .4s ease' }} />
        </div>
      </div>
    </div>
  );
}

function StepShell({ eyebrow, question, hint, children }) {
  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '72px 40px 40px' }}>
      <div style={{
        fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
        color: PALETTE.accent, letterSpacing: '0.12em', marginBottom: 16, textTransform: 'uppercase',
      }}>{eyebrow}</div>
      <h1 style={{
        fontFamily: 'Instrument Serif, serif', fontWeight: 400,
        fontSize: 52, lineHeight: 1.14, letterSpacing: '-0.02em',
        margin: '0 0 20px', color: PALETTE.ink,
      }}>{question}</h1>
      {hint && <p style={{ fontSize: 16, color: PALETTE.ink2, lineHeight: 1.55, margin: '0 0 36px', maxWidth: 540 }}>{hint}</p>}
      {children}
    </div>
  );
}

function TextField({ value, onChange, placeholder, prefix, large, autoFocus }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: large ? '18px 20px' : '14px 18px',
      background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 12,
      fontSize: large ? 18 : 15, color: PALETTE.ink,
    }}>
      {prefix && <span style={{ color: PALETTE.ink3 }}>{prefix}</span>}
      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        style={{
          flex: 1, border: 'none', outline: 'none', background: 'transparent',
          fontFamily: 'Inter, sans-serif', fontSize: 'inherit', color: PALETTE.ink,
        }}
      />
    </label>
  );
}

function ChipChoice({ options, value, onChange, columns = 3 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 10 }}>
      {options.map((o) => {
        const active = value === o.value;
        const disabled = o.disabled;
        return (
          <button key={o.value} disabled={disabled} onClick={() => !disabled && onChange(o.value)} style={{
            padding: '18px 18px', textAlign: 'left',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.4 : 1,
            background: active ? PALETTE.ink : PALETTE.surface,
            color: active ? '#FAF8F5' : PALETTE.ink,
            border: `1px solid ${active ? PALETTE.ink : PALETTE.line}`,
            borderRadius: 12, fontFamily: 'Inter, sans-serif',
            display: 'flex', flexDirection: 'column', gap: 4,
            transition: 'all .15s',
            boxShadow: active ? '0 4px 16px rgba(26,23,20,0.12)' : 'none',
          }}>
            <span style={{ fontSize: 22, marginBottom: 4 }}>{o.glyph}</span>
            <span style={{ fontSize: 14, fontWeight: 500, letterSpacing: '-0.01em' }}>{o.label}</span>
            {o.sub && <span style={{ fontSize: 12, color: active ? 'rgba(250,248,245,0.6)' : PALETTE.ink3 }}>{o.sub}</span>}
          </button>
        );
      })}
    </div>
  );
}

function BudgetSlider({ value, onChange }) {
  const min = 500, max = 10000, step = 100;
  const pct = ((value - min) / (max - min)) * 100;
  const tier = value < 1500 ? 'Starter' : value < 3500 ? 'Growth' : value < 7000 ? 'Scale' : 'Dominate';
  const est = Math.round(value / 45);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <div style={{
            fontFamily: 'Instrument Serif, serif', fontSize: 72, lineHeight: 1, color: PALETTE.ink, letterSpacing: '-0.02em',
          }}>
            ${value.toLocaleString()}<span style={{ fontSize: 22, color: PALETTE.ink3 }}>/mo</span>
          </div>
          <div style={{ fontSize: 13, color: PALETTE.ink3, marginTop: 8, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            {tier} tier
          </div>
        </div>
        <div style={{
          padding: '16px 20px', background: PALETTE.accentTint,
          border: `1px solid ${PALETTE.accentSoft}`, borderRadius: 12, textAlign: 'right',
        }}>
          <div style={{ fontSize: 11, color: PALETTE.accentDeep, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Est. leads / month</div>
          <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 38, color: PALETTE.accentDeep, lineHeight: 1 }}>
            {Math.max(0, est - 8)}–{est + 8}
          </div>
        </div>
      </div>

      <div style={{ position: 'relative', height: 48, marginBottom: 8 }}>
        <div style={{ position: 'absolute', top: 22, left: 0, right: 0, height: 4, background: PALETTE.surfaceAlt, borderRadius: 2 }}/>
        <div style={{ position: 'absolute', top: 22, left: 0, width: `${pct}%`, height: 4, background: PALETTE.accent, borderRadius: 2 }}/>
        <input type="range" min={min} max={max} step={step} value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10))}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, width: '100%', height: 48, appearance: 'none', background: 'transparent', cursor: 'pointer' }}
        />
        <div style={{
          position: 'absolute', top: 12, left: `calc(${pct}% - 12px)`,
          width: 24, height: 24, borderRadius: '50%', background: PALETTE.surface,
          border: `2px solid ${PALETTE.accent}`, boxShadow: '0 2px 8px rgba(184,80,26,0.2)', pointerEvents: 'none',
        }}/>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.03em' }}>
        <span>$500</span><span>$3,000</span><span>$6,000</span><span>$10,000</span>
      </div>
    </div>
  );
}

function slugFromUrl(url) {
  if (!url) return '';
  let host = url.trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
  if (host.startsWith('www.')) host = host.slice(4);
  const root = (host.split('.')[0] || '').toLowerCase().replace(/[^a-z0-9-]/g, '');
  return root.slice(0, 50);
}

function nameFromSlug(slug) {
  return slug.replace(/-+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeWebsiteUrl(url) {
  const trimmed = String(url || '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function getActiveCompanyId(session) {
  const memberships = session?.user?.memberships || [];
  return session?.user?.activeCompany?.id || session?.user?.tenant?.companyId || memberships[0]?.companyId || '';
}

function tenantOptions(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

function normalizeSaasBrands(brands = []) {
  return brands.map((brand) => ({
    id: brand.id,
    slug: brand.slug || brand.settings?.legacyBrandSlug || brand.id,
    name: brand.name || brand.slug || 'Untitled brand',
    website: brand.websiteUrl || brand.settings?.website || '',
    tagline: brand.settings?.tagline || 'Tenant-owned brand',
    location: brand.settings?.location || '',
  }));
}

function selectedOfferName(offers, offerSlug) {
  return offers.find((offer) => offer.offer === offerSlug)?.name || offerSlug;
}

function cityLabel(city) {
  return String(city || '').split('+')[0].trim() || 'Service area';
}

function campaignNameFromInput(data, offers) {
  const offer = selectedOfferName(offers, data.offer);
  const service = SERVICE_LABELS[data.projectType] || data.projectType || 'Campaign';
  return `${offer} - ${service} - ${cityLabel(data.city)}`;
}

export default function Wizard({ navigate, session = {} }) {
  const isSaasSession = session?.user?.auth?.type === 'supabase';
  const activeCompanyId = getActiveCompanyId(session);
  const isAdmin = !session?.user?.brands; // null brands = admin
  const [step, setStep] = useState(0);
  // mode: 'new' (provision a new brand from URL) | 'existing' (pick from list)
  const [data, setData] = useState({
    mode: 'new',
    brandId: '', brandSlug: '', brandName: '', website: '',
    projectType: '', country: 'CA', city: '', budgetMonthly: 2000, offer: '',
  });
  const [clients, setClients] = useState([]);
  const [offers, setOffers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [launchFeedback, setLaunchFeedback] = useState(null);
  const [provisionStatus, setProvisionStatus] = useState(null);

  useEffect(() => {
    setLaunchFeedback(null);
    setSubmitError(null);
    if (isSaasSession) {
      if (!activeCompanyId) {
        setClients([]);
        setOffers([]);
        setSubmitError('Select an active company before building a campaign.');
        return;
      }
      const options = tenantOptions(activeCompanyId);
      api.get('/api/saas/brands', options)
        .then((r) => setClients(normalizeSaasBrands(r.brands || [])))
        .catch((err) => setSubmitError(safeFailureMessage(err, 'campaign-launch')));
      api.get('/api/saas/offers', options)
        .then((r) => setOffers(r.offers || []))
        .catch((err) => setSubmitError(safeFailureMessage(err, 'campaign-launch')));
      return;
    }

    api.get('/api/clients').then((r) => setClients(r.clients || [])).catch(() => {});
    api.get('/api/offers').then((r) => setOffers(r.offers || [])).catch(() => {});
  }, [isSaasSession, activeCompanyId]);

  const update = (patch) => {
    setLaunchFeedback(null);
    setData((d) => ({ ...d, ...patch }));
  };

  function changeStep(nextStep) {
    setLaunchFeedback(null);
    setStep(nextStep);
  }

  // Filter project types to those any offer supports — keeps the chips honest.
  const supportedProjectTypes = useMemo(() => {
    const set = new Set();
    for (const o of offers) (o.supportedProjectTypes || []).forEach((p) => set.add(p));
    return set;
  }, [offers]);

  const offersForProject = useMemo(() => offers.filter((o) =>
    !data.projectType || (o.supportedProjectTypes || []).includes(data.projectType)
  ), [offers, data.projectType]);

  const newSlug = data.mode === 'new' ? slugFromUrl(data.website) : '';
  const canAdvance = {
    0: data.mode === 'new' ? newSlug.length >= 3 : (isSaasSession ? !!data.brandId : !!data.brandSlug),
    1: !!data.projectType,
    2: COUNTRY_OPTIONS.some((item) => item.value === data.country)
      && (data.city || '').trim().length > 1,
    3: data.budgetMonthly >= 500,
    4: !!data.offer,
  }[step];

  async function submitLegacy() {
      let { brandSlug, brandName } = data;
      if (data.mode === 'new') {
        brandSlug = newSlug;
        brandName = brandName || nameFromSlug(brandSlug);
        // Brand research first — the workflow's first step requires
        // BRAND-RESEARCH.json. Without this, a fresh brand fails at "Writing
        // your marketing plan" with "Brand research not found".
        setProvisionStatus('Researching your brand…');
        const research = await streamBrandResearch({
          url: data.website,
          onProgress: (evt) => {
            setProvisionStatus(evt.label || `Brand research ${evt.pct || 0}%`);
          },
        });
        // Use the actual company name from research if available, falling
        // back to the slug-derived name. Slug stays driven by the URL.
        brandName = research?.company?.name || brandName;
        await streamProvision({
          brandSlug,
          brandName,
          onPhase: (evt) => {
            const base = PROVISION_PHASE_LABELS[evt.phase] || evt.phase;
            const detail = evt.state ? ` (${evt.state})` : '';
            setProvisionStatus(base + detail);
          },
        });
        // Grant the current user access to the brand they just created.
        // For admins (brands: null) the server is a no-op; for restricted users
        // it adds the slug and returns a fresh token we store immediately.
        const grant = await api.post('/api/users/me/brands', { brandSlug });
        if (grant.token) {
          setToken(grant.token);
          localStorage.setItem('lp_user', JSON.stringify(grant.user));
        }
      }
      const res = await api.post('/api/workflows', {
        brandSlug,
        brandName,
        offer: data.offer,
        projectType: data.projectType,
        options: {
          website: data.website || undefined,
          city: data.city,
          country: data.country,
          budgetMonthly: data.budgetMonthly,
        },
      });
      navigate(`building/${brandSlug}/${res.id}`);
  }

  async function submitSaas() {
    if (!activeCompanyId) throw new Error('active_company_required');
    const options = tenantOptions(activeCompanyId);
    let selectedBrand = null;

    if (data.mode === 'new') {
      const brandSlug = newSlug;
      const brandName = data.brandName || nameFromSlug(brandSlug);
      const existingBrand = clients.find((brand) => brand.slug === brandSlug);
      if (existingBrand) {
        setProvisionStatus('Using tenant-owned brand record');
        selectedBrand = existingBrand;
      } else {
        setProvisionStatus('Creating tenant-owned brand record');
        const createdBrand = await api.post('/api/saas/brands', {
          name: brandName,
          slug: brandSlug,
          websiteUrl: normalizeWebsiteUrl(data.website),
          settings: {
            source: 'campaign_wizard',
            legacyBrandSlug: brandSlug,
            approvalRequired: true,
          },
        }, options);
        const normalizedBrand = normalizeSaasBrands([createdBrand.brand])[0];
        selectedBrand = { ...(createdBrand.brand || {}), ...(normalizedBrand || {}) };
        if (normalizedBrand?.id) {
          setClients((previous) => (
            previous.some((brand) => brand.id === normalizedBrand.id || brand.slug === normalizedBrand.slug)
              ? previous
              : [...previous, normalizedBrand]
          ));
        }
      }
    } else {
      selectedBrand = clients.find((brand) => brand.id === data.brandId);
    }

    if (!selectedBrand?.id) throw new Error('brand_not_found');

    setProvisionStatus('Checking launch readiness');
    const readiness = await api.get(
      `/api/saas/onboarding/readiness?projectType=${encodeURIComponent(data.projectType)}`,
      options,
    );
    if (!readiness?.checklist?.ready) {
      throw createCampaignLaunchReadinessError(readiness?.checklist);
    }

    setProvisionStatus('Saving tenant-owned campaign draft');
    const campaignPayload = {
      brandId: selectedBrand.id,
      name: campaignNameFromInput(data, offers),
      projectType: data.projectType,
      offer: data.offer,
      status: 'draft',
      brief: {
        source: 'campaign_wizard',
        objective: 'generate_qualified_leads',
        serviceArea: data.city,
        country: data.country,
        projectType: data.projectType,
        offer: data.offer,
        budgetMonthly: data.budgetMonthly,
      },
      settings: {
        source: 'campaign_wizard',
        city: data.city,
        country: data.country,
        serviceArea: data.city,
        budgetMonthly: data.budgetMonthly,
        offerName: selectedOfferName(offers, data.offer),
        approvalRequired: true,
        autoPublish: false,
        launchOptions: {
          provider: 'meta',
          approvalRequired: true,
          autoPublish: false,
        },
        legacyBrandSlug: selectedBrand.slug || newSlug || null,
        campaignDefaults: {
          city: data.city,
          country: data.country,
          dailyBudget: Math.max(1, Math.round(data.budgetMonthly / 30)),
        },
      },
    };
    const createdCampaign = await api.post('/api/saas/campaigns', campaignPayload, options);
    const campaign = createdCampaign.campaign;

    setProvisionStatus('Creating tenant workflow job');
    const workflowPayload = {
      brandId: selectedBrand.id,
      campaignId: campaign.id,
      currentStep: 'brief',
      input: {
        source: 'campaign_wizard',
        brandSlug: selectedBrand.slug || newSlug,
        brandName: selectedBrand.name || data.brandName || nameFromSlug(selectedBrand.slug || newSlug),
        offer: data.offer,
        projectType: data.projectType,
        options: {
          website: selectedBrand.websiteUrl || selectedBrand.website || data.website || undefined,
          city: data.city,
          country: data.country,
          budgetMonthly: data.budgetMonthly,
          approvalRequired: true,
          autoPublish: false,
        },
      },
      idempotencyKey: `workflow:campaign_generation:${activeCompanyId}:${campaign.id}`,
    };
    const createdWorkflow = await api.post('/api/saas/workflows', workflowPayload, options);
    const workflowJob = createdWorkflow.workflowJob;

    setProvisionStatus('Starting tenant workflow');
    await api.post(`/api/saas/workflows/${workflowJob.id}/start`, {}, options);
    navigate(`workflow/${workflowJob.id}`);
  }

  async function submit() {
    setSubmitting(true);
    setSubmitError(null);
    setLaunchFeedback(null);
    setProvisionStatus(null);
    try {
      if (isSaasSession) {
        await submitSaas();
      } else {
        await submitLegacy();
      }
    } catch (err) {
      const readinessFailure = isSaasSession ? campaignLaunchReadinessPresentation(err) : null;
      setLaunchFeedback(readinessFailure);
      setSubmitError(readinessFailure ? null : (isSaasSession
        ? safeFailureMessage(err, 'campaign-launch')
        : err.message));
      setSubmitting(false);
      setProvisionStatus(null);
    }
  }

  let body = null;
  if (step === 0) {
    const isNew = data.mode === 'new';
    const isExisting = data.mode === 'existing';
    body = (
      <StepShell eyebrow="Step 01 — Your business" question="Which business is this campaign for?"
        hint={isSaasSession
          ? 'New brands create tenant-owned records. Existing brands reuse active company records only.'
          : "A new brand spins up its own funnel site automatically. Existing brands reuse what's already provisioned."}>

        {/* NEW BRAND PATH */}
        <button
          type="button"
          onClick={() => update({ mode: 'new', brandId: '', brandSlug: '', brandName: '' })}
          style={{
            width: '100%', textAlign: 'left', cursor: 'pointer',
            padding: '22px 24px',
            background: isNew ? PALETTE.surface : PALETTE.surfaceAlt,
            border: `1.5px solid ${isNew ? PALETTE.accent : PALETTE.line}`,
            borderRadius: 14,
            boxShadow: isNew ? `0 0 0 4px ${PALETTE.accentTint}` : 'none',
            fontFamily: 'Inter, sans-serif',
            transition: 'all .15s',
            marginBottom: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isNew ? 16 : 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5,
                color: isNew ? PALETTE.accent : PALETTE.ink3,
                letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 500,
              }}>
                {isNew ? '● New brand' : '○ New brand'}
              </span>
            </div>
            <span style={{ fontSize: 11.5, color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace' }}>
              {isSaasSession ? 'Draft first, launch gated' : "We'll build the site"}
            </span>
          </div>
          <div style={{ fontSize: 16, color: PALETTE.ink, letterSpacing: '-0.01em', marginBottom: isNew ? 14 : 0 }}>
            {isSaasSession
              ? "Enter your website - we'll save a tenant-owned brand before creating the campaign draft."
              : "Enter your website - we'll create the brand, scrape your business info, and provision a funnel site."}
          </div>
          {isNew && (
            <div onClick={(e) => e.stopPropagation()}>
              <TextField
                large autoFocus prefix="https://"
                placeholder="yourcompany.com"
                value={data.website}
                onChange={(v) => update({ website: v })}
              />
              {newSlug && (
                <div style={{ marginTop: 10, fontSize: 12, color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace' }}>
                  → brand slug: <span style={{ color: PALETTE.accent }}>{newSlug}</span>
                  {!isSaasSession && (
                    <>
                      {' · '}repo: <span style={{ color: PALETTE.ink2 }}>leadphoenixai/{newSlug}-site</span>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </button>

        {/* DIVIDER */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0',
          fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5, color: PALETTE.ink3,
          letterSpacing: '0.16em', textTransform: 'uppercase',
        }}>
          <div style={{ flex: 1, height: 1, background: PALETTE.lineSoft }} />
          <span>or choose existing</span>
          <div style={{ flex: 1, height: 1, background: PALETTE.lineSoft }} />
        </div>

        {/* EXISTING BRAND PATH — brand grid always visible, click a card to select */}
        <div style={{
          padding: '20px 24px',
          background: isExisting ? PALETTE.surface : PALETTE.surfaceAlt,
          border: `1.5px solid ${isExisting ? PALETTE.accent : PALETTE.line}`,
          borderRadius: 14,
          boxShadow: isExisting ? `0 0 0 4px ${PALETTE.accentTint}` : 'none',
          fontFamily: 'Inter, sans-serif',
          transition: 'all .15s',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <span style={{
              fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5,
              color: isExisting ? PALETTE.accent : PALETTE.ink3,
              letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 500,
            }}>
              {isExisting ? '● Existing brand' : '○ Existing brand'}
            </span>
            <span style={{ fontSize: 11.5, color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace' }}>
              {clients.length} {clients.length === 1 ? 'brand' : 'brands'} ready
            </span>
          </div>
          {clients.length === 0 ? (
            <div style={{ padding: '8px 0', color: PALETTE.ink3, fontSize: 13.5 }}>
              No brands yet — start a new one above.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              {clients.map((c) => {
                const active = isExisting && data.brandSlug === c.slug;
                return (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => update({ mode: 'existing', brandId: c.id || '', brandSlug: c.slug, brandName: c.name, website: c.website || '' })}
                    style={{
                      padding: '14px 16px', textAlign: 'left', cursor: 'pointer',
                      background: active ? PALETTE.ink : PALETTE.surface,
                      color: active ? '#FAF8F5' : PALETTE.ink,
                      border: `1px solid ${active ? PALETTE.ink : PALETTE.line}`,
                      borderRadius: 10, fontFamily: 'Inter, sans-serif',
                      transition: 'all .12s',
                    }}
                  >
                    <div style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: '-0.01em' }}>{c.name}</div>
                    {c.tagline && <div style={{ fontSize: 12, color: active ? 'rgba(250,248,245,0.65)' : PALETTE.ink3, marginTop: 3 }}>{c.tagline}</div>}
                    {c.location && <div style={{ fontSize: 11, color: active ? 'rgba(250,248,245,0.5)' : PALETTE.ink3, marginTop: 2 }}>{c.location}</div>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </StepShell>
    );
  } else if (step === 1) {
    const allTypes = ['kitchen', 'bathroom', 'basement', 'addition', 'roof', 'windows', 'deck', 'flooring', 'sunroom'];
    const options = allTypes.map((p) => ({
      value: p,
      glyph: SERVICE_GLYPHS[p] || '◆',
      label: SERVICE_LABELS[p] || p,
      disabled: !supportedProjectTypes.has(p),
      sub: supportedProjectTypes.has(p) ? null : 'No offer supports this yet',
    }));
    body = (
      <StepShell eyebrow="Step 02 — What you sell" question="Which service is this campaign for?"
        hint="Pick the one you most want leads for. Greyed-out options have no offer template yet.">
        <ChipChoice columns={3} value={data.projectType} onChange={(v) => update({ projectType: v, offer: '' })} options={options} />
      </StepShell>
    );
  } else if (step === 2) {
    const geography = COUNTRY_CITY_PRESETS[data.country] || COUNTRY_CITY_PRESETS.CA;
    body = (
      <StepShell eyebrow="Step 03 — Where you work" question="Which area do you serve?"
        hint="We'll show your ads to homeowners in this region. Tighter targeting means cheaper leads.">
        <ChipChoice
          columns={3}
          value={data.country}
          onChange={(country) => update({ country, city: '' })}
          options={COUNTRY_OPTIONS}
        />
        <div style={{ marginTop: 20 }}>
          <TextField large autoFocus prefix={<IconMap size={18} />} placeholder={geography.placeholder} value={data.city} onChange={(v) => update({ city: v })} />
        </div>
        <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          {geography.cities.map((city) => (
            <button key={city} onClick={() => update({ city })} style={{
              padding: '10px 14px', background: PALETTE.surface, border: `1px solid ${PALETTE.line}`,
              borderRadius: 8, cursor: 'pointer', fontSize: 13, color: PALETTE.ink2,
              fontFamily: 'Inter, sans-serif', textAlign: 'left',
            }}>+ {city}</button>
          ))}
        </div>
      </StepShell>
    );
  } else if (step === 3) {
    body = (
      <StepShell eyebrow="Step 04 — Your budget" question="How much will you spend per month?"
        hint="This is your ad spend (paid to Meta), not our fee. Most renovators start around $2,000/mo and scale once leads roll in.">
        <BudgetSlider value={data.budgetMonthly} onChange={(v) => update({ budgetMonthly: v })} />
      </StepShell>
    );
  } else if (step === 4) {
    const options = offersForProject.map((o) => ({
      value: o.offer,
      glyph: o.icon || '◆',
      label: o.name || o.offer,
      sub: o.tagline || '',
    }));
    body = (
      <StepShell eyebrow="Step 05 — Your hook" question="What's your offer?"
        hint="The single thing that makes a homeowner click. Pick one. Loaded from your offer-templates library.">
        {options.length === 0 ? (
          <div style={{ padding: 18, color: PALETTE.ink3, fontSize: 14 }}>
            No offer templates support {data.projectType || 'this project type'} yet.
          </div>
        ) : (
          <ChipChoice columns={2} value={data.offer} onChange={(v) => update({ offer: v })} options={options} />
        )}
      </StepShell>
    );
  }

  return (
    <div style={{ flex: 1, background: PALETTE.bg, display: 'flex', flexDirection: 'column', overflow: 'auto', height: '100vh' }}>
      <WizardHeader step={step} onExit={() => navigate('home')} />
      <div style={{ flex: 1 }}>{body}</div>
      <div style={{
        position: 'sticky', bottom: 0, borderTop: `1px solid ${PALETTE.lineSoft}`,
        background: PALETTE.bg, padding: '14px 32px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
        flexWrap: 'wrap',
      }}>
        <Button variant="ghost" onClick={() => step === 0 ? navigate('home') : changeStep(step - 1)}>← Back</Button>
        <div style={{
          minWidth: 0,
          flex: '1 1 280px',
          textAlign: 'center',
          fontSize: 12,
          color: PALETTE.ink3,
          fontFamily: provisionStatus ? 'JetBrains Mono, monospace' : 'inherit',
        }}>
          {(launchFeedback?.message || submitError) ? (
            <span style={{ color: PALETTE.danger, display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
              <span>{launchFeedback?.message || submitError}</span>
              {launchFeedback?.actionRoute && (
                <button
                  type="button"
                  onClick={() => navigate(launchFeedback.actionRoute)}
                  style={{
                    appearance: 'none',
                    border: 0,
                    borderBottom: `1px solid ${PALETTE.danger}`,
                    padding: 0,
                    background: 'transparent',
                    color: PALETTE.danger,
                    cursor: 'pointer',
                    font: 'inherit',
                    fontWeight: 600,
                  }}
                >
                  {launchFeedback.actionLabel}
                </button>
              )}
            </span>
          )
            : provisionStatus ? <span style={{ color: PALETTE.accent }}>● {provisionStatus}…</span>
            : step < TOTAL_STEPS - 1
              ? `${TOTAL_STEPS - step - 1} questions left`
              : isSaasSession ? 'Last one - then we save the draft and check readiness.' : 'Last one - then we build.'}
        </div>
        {step < TOTAL_STEPS - 1 ? (
          <Button variant="primary" onClick={() => changeStep(step + 1)} disabled={!canAdvance}>
            Continue <IconChevron size={14}/>
          </Button>
        ) : (
          <Button variant="accent" onClick={submit} disabled={!canAdvance || submitting}>
            <IconSpark size={14}/> {submitting
              ? (isSaasSession ? 'Saving campaign…' : (data.mode === 'new' ? 'Provisioning brand site…' : 'Starting…'))
              : 'Build my campaign'}
          </Button>
        )}
      </div>
    </div>
  );
}
